import { useEffect, useMemo, useState } from 'react';
import { CiWarning } from 'react-icons/ci';

import { Modal } from '@/components';
import { Button, ButtonOutlined } from '@/components/Button';
import { Input } from '@/components/Inputs';

import styles from './EmailConflictModal.module.scss';

interface EmailConflictModalProps {
  open: boolean;
  email: string;
  onClose: () => void;
  onContinue: (email: string) => void;
  isChecking: boolean;
}

const EmailConflictModal = ({
  open,
  email,
  onClose,
  onContinue,
  isChecking,
}: EmailConflictModalProps) => {
  // Only the local part is editable — the domain is the internal one every vessel shares.
  const { initialLocalPart, domain } = useMemo(() => {
    const atIndex = email.indexOf('@');
    if (atIndex !== -1) {
      return {
        initialLocalPart: email.substring(0, atIndex),
        domain: email.substring(atIndex), // includes the @
      };
    }
    return { initialLocalPart: email, domain: '' };
  }, [email]);

  const [localPart, setLocalPart] = useState(initialLocalPart);

  // A rejected suggestion comes back as a new `email`, so reset to whatever is taken now.
  useEffect(() => {
    setLocalPart(initialLocalPart);
  }, [initialLocalPart]);

  const unchanged = localPart === initialLocalPart;
  const canContinue = !isChecking && !!localPart.trim() && !unchanged;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canContinue) return;
    onContinue(localPart.trim() + domain);
  };

  return (
    <Modal open={open} onClose={onClose} title="Email Already Exists">
      <form className={styles.form} onSubmit={handleSubmit}>
        <p className={styles.icon}>
          <CiWarning />
        </p>
        <p className={styles.message}>
          <strong>{email}</strong> is already taken. Pick a different address to continue.
        </p>
        <Input
          label="Vessel Email"
          mandatory
          autoFocus
          value={localPart}
          onChange={(e) => setLocalPart(e.target.value)}
          placeholder="my.vessel"
        />
        {/* Muted helper rather than a red error — the field opens on the taken value, so an
            error state would fire before the user has typed anything. */}
        <p className={styles.preview}>
          {unchanged ? (
            'Please change the address to continue.'
          ) : (
            <>
              Full address: <span>{localPart.trim() + domain}</span>
            </>
          )}
        </p>
        <div className={styles.actions}>
          <Button type="submit" disabled={!canContinue}>
            {isChecking ? 'Checking…' : 'Continue'}
          </Button>
          <ButtonOutlined type="button" onClick={onClose}>
            Cancel
          </ButtonOutlined>
        </div>
      </form>
    </Modal>
  );
};

export default EmailConflictModal;
